import React, { useEffect, useState } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
//  LIGHT / DARK
//
//  Three states, one button. No stored choice means follow the operating
//  system, which is the right default: a tech whose phone is dark at 9pm
//  should not be handed a white screen. Pressing it pins a choice, and the
//  choice survives a reload.
//
//  Every localStorage call is wrapped. In a private window, with site data
//  blocked, or inside some in-app browsers, reading it throws rather than
//  returning null — and a theme button is not worth a blank screen.
// ═══════════════════════════════════════════════════════════════════════════

const KEY = 'qps-theme';

const read = () => {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch { return null; }
};

const write = (v) => {
  try {
    if (v) localStorage.setItem(KEY, v);
    else localStorage.removeItem(KEY);
  } catch { /* nothing to do, and nothing worth breaking over */ }
};

// Applied to <html>, because the tokens live on :root. Removing the attribute
// hands control back to the media query rather than pinning light.
const apply = (v) => {
  const el = document.documentElement;
  if (v) el.setAttribute('data-theme', v);
  else el.removeAttribute('data-theme');
};

const systemPrefersDark = () => {
  try { return window.matchMedia('(prefers-color-scheme: dark)').matches; }
  catch { return false; }
};

export default function ThemeToggle({ className = 'qps-theme-btn' }) {
  const [choice, setChoice] = useState(read);

  useEffect(() => { apply(choice); }, [choice]);

  // With no stored choice we are following the system, so re-render when the
  // system flips — otherwise the icon keeps offering the mode already on.
  const [systemDark, setSystemDark] = useState(systemPrefersDark);
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia('(prefers-color-scheme: dark)'); } catch { return undefined; }
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.addEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);

  const isDark = choice ? choice === 'dark' : systemDark;

  const toggle = () => {
    const next = isDark ? 'light' : 'dark';
    setChoice(next);
    write(next);
  };

  // The label says what pressing it will DO, not what the screen is now.
  const label = isDark ? 'Switch to light mode' : 'Switch to dark mode';

  return (
    <button type="button" className={className} onClick={toggle} title={label} aria-label={label}>
      {isDark ? (
        // Sun: press for light.
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2.6v2.2M12 19.2v2.2M4.3 4.3l1.6 1.6M18.1 18.1l1.6 1.6M2.6 12h2.2M19.2 12h2.2M4.3 19.7l1.6-1.6M18.1 5.9l1.6-1.6" />
        </svg>
      ) : (
        // Moon: press for dark.
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20.5 14.3A8.6 8.6 0 1 1 9.7 3.5a6.8 6.8 0 0 0 10.8 10.8z" />
        </svg>
      )}
    </button>
  );
}
